import React, { useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, Tabs, Tab, TextField, Select,
  MenuItem, FormControl, InputLabel, Radio, RadioGroup, FormControlLabel,
  FormLabel, Switch, Button, Divider, Table, TableHead, TableRow, TableCell,
  TableBody, Tooltip, LinearProgress, Alert, Checkbox,
} from '@mui/material';
import {
  PlayArrow as PlayIcon, SkipNext as StepIcon, Pause as PauseIcon,
  Stop as StopIcon, FiberManualRecord as RecordIcon, Add as AddIcon,
  CheckCircle as PassIcon, Cancel as FailIcon, HourglassEmpty as RunningIcon,
  Circle as PendingIcon, DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon, ChevronRight as ChevronRightIcon,
  Extension as ExtensionIcon, OpenInNew as OpenInNewIcon,
  DesktopWindows as DesktopIcon, Laptop as LaptopIcon,
  Delete as DeleteIcon, Undo as UndoIcon, Save as SaveIcon,
  ArrowUpward as UpIcon, ArrowDownward as DownIcon, Close as CloseIcon,
} from '@mui/icons-material';

const FILTER_STEPS = [
  { num: 1, type: 'navigate', name: 'Navigate to content filter page', status: 'passed', selector: '', value: 'https://admin.securly.com/content-filter', timeout: 10000 },
  { num: 2, type: 'wait', name: 'Wait for filter dashboard', status: 'passed', selector: '.filter-dashboard', value: '', timeout: 5000 },
  { num: 3, type: 'click', name: 'Click "Student Policies" tab', status: 'passed', selector: 'button[data-tab="student-policies"]', value: '', timeout: 3000 },
  { num: 4, type: 'wait', name: 'Wait for policy list', status: 'passed', selector: '.policy-list-container', value: '', timeout: 5000 },
  { num: 5, type: 'click', name: 'Select target policy', status: 'running', selector: '.policy-row:contains("{{policy_name}}")', value: '', timeout: 3000 },
  { num: 6, type: 'script', name: 'Extract current filter rules', status: 'pending', selector: '', value: 'return document.querySelectorAll(".filter-rule").map(r => ({category: r.dataset.category, action: r.dataset.action}))', timeout: 10000 },
  { num: 7, type: 'click', name: 'Click "Edit Policy" button', status: 'pending', selector: 'button.edit-policy-btn', value: '', timeout: 3000 },
  { num: 8, type: 'type', name: 'Update allowed domains', status: 'pending', selector: '#allowed-domains-input', value: '{{allowed_domains}}', timeout: 3000 },
  { num: 9, type: 'click', name: 'Save changes', status: 'pending', selector: 'button[type="submit"].save-btn', value: '', timeout: 5000 },
  { num: 10, type: 'assert', name: 'Verify save success toast', status: 'pending', selector: '.toast-success', value: 'exists', timeout: 8000 },
];

const STEP_TYPES = [
  { value: 'navigate', label: 'Navigate' },
  { value: 'type', label: 'Type' },
  { value: 'click', label: 'Click' },
  { value: 'wait', label: 'Wait (Selector)' },
  { value: 'wait_nav', label: 'Wait (Navigation)' },
  { value: 'assert', label: 'Assert URL' },
  { value: 'assert_element', label: 'Assert Element' },
  { value: 'script', label: 'Script' },
  { value: 'select', label: 'Select' },
];

const FAILURE_OPTIONS = [
  { value: 'stop', label: 'Stop execution' },
  { value: 'skip', label: 'Skip this step and continue' },
  { value: 'jump', label: 'Jump to step...' },
  { value: 'retry_stop', label: 'Retry N times then stop' },
  { value: 'retry_skip', label: 'Retry N times then skip' },
];

const TYPE_CHIP_COLORS = {
  navigate: { bg: '#e3f2fd', color: '#1565c0' },
  wait: { bg: '#fff3e0', color: '#e65100' },
  wait_nav: { bg: '#fff3e0', color: '#e65100' },
  type: { bg: '#e3f2fd', color: '#1565c0' },
  click: { bg: '#e3f2fd', color: '#1565c0' },
  assert: { bg: '#e8f5e9', color: '#2e7d32' },
  assert_element: { bg: '#e8f5e9', color: '#2e7d32' },
  script: { bg: '#f3e5f5', color: '#6a1b9a' },
  select: { bg: '#e3f2fd', color: '#1565c0' },
};

const STATUS_CONFIGS = {
  passed: { icon: <PassIcon sx={{ fontSize: 18, color: '#4caf50' }} />, bg: '#f1f8e9' },
  running: { icon: <RunningIcon sx={{ fontSize: 18, color: '#ff9800' }} />, bg: '#fff8e1', border: '2px solid #ffc107' },
  pending: { icon: <PendingIcon sx={{ fontSize: 14, color: '#bdbdbd' }} />, bg: 'transparent' },
};

const MOCK_EXECUTIONS = [
  { id: 401, date: '2026-04-09 10:15', status: 'passed', duration: '9.1s', credential: 'admin @ fivekeys', steps: '10/10' },
  { id: 402, date: '2026-04-08 14:30', status: 'failed', duration: '15.2s', credential: 'admin @ riverside', steps: '6/10' },
  { id: 403, date: '2026-04-07 11:45', status: 'passed', duration: '8.7s', credential: 'admin @ sandi', steps: '10/10' },
];

const MOCK_OUTPUT = {
  scriptId: 15,
  scriptName: 'Content Filter Policy Update',
  executionId: 'exec-20260409-1015',
  status: 'running',
  startedAt: '2026-04-09T10:15:02.000Z',
  credential: { username: 'admin', client: 'fivekeys' },
  stepsCompleted: 4,
  stepsTotal: 10,
  currentStep: { num: 5, name: 'Select target policy', elapsed: '1.3s' },
  extractedData: {
    currentRules: [
      { category: 'Social Media', action: 'block' },
      { category: 'Gaming', action: 'block' },
      { category: 'Educational', action: 'allow' },
    ],
  },
};

const TabPanel = ({ children, value, index, ...rest }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ p: 0 }} {...rest}>
    {value === index && children}
  </Box>
);

/* --- Reusable step editing fields by type --- */

const SelectorField = ({ value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Selector</Typography>
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <TextField size="small" fullWidth value={value} variant="outlined"
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
      <Tooltip title="Highlight element on page"><IconButton size="small" sx={{ bgcolor: '#e3f2fd', fontSize: '0.9rem' }}>&#127919;</IconButton></Tooltip>
      <Tooltip title="Pick element from page"><IconButton size="small" sx={{ bgcolor: '#e3f2fd', fontSize: '0.9rem' }}>&#128204;</IconButton></Tooltip>
    </Box>
  </Box>
);

const ValueFieldWithCred = ({ value, label = 'Value' }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>{label}</Typography>
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <TextField size="small" fullWidth value={value} variant="outlined"
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
      <Tooltip title="Insert {{credential.username}}">
        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 0.8, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{'{{user}}'}</Button>
      </Tooltip>
      <Tooltip title="Insert {{credential.password}}">
        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 0.8, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{'{{pass}}'}</Button>
      </Tooltip>
    </Box>
  </Box>
);

const TimeoutField = ({ value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Timeout (ms)</Typography>
    <TextField size="small" value={value} type="number" variant="outlined"
      sx={{ width: 120, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
  </Box>
);

const FailureHandlingField = ({ steps }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>On Failure</Typography>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select value="stop" variant="outlined" sx={{ fontSize: '0.8rem' }}>
          {FAILURE_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  </Box>
);

const StepTypeFields = ({ step, steps }) => {
  const t = step.type;
  switch (t) {
    case 'navigate':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>URL</Typography>
            <TextField size="small" fullWidth value={step.value || ''} variant="outlined"
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
          </Box>
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'type':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Name</Typography>
            <TextField size="small" fullWidth value={step.name} variant="outlined" sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
          </Box>
          <SelectorField value={step.selector} />
          <ValueFieldWithCred value={step.value} />
          <FormControlLabel
            control={<Checkbox size="small" />}
            label={<Typography variant="caption">Clear field first</Typography>}
            sx={{ mb: 1 }}
          />
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'click':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Name</Typography>
            <TextField size="small" fullWidth value={step.name} variant="outlined" sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
          </Box>
          <SelectorField value={step.selector} />
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'wait':
      return (
        <>
          <SelectorField value={step.selector} />
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'wait_nav':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Expected URL (optional)</Typography>
            <TextField size="small" fullWidth value={step.value || ''} variant="outlined"
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
          </Box>
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'assert':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Operator</Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value="contains" variant="outlined" sx={{ fontSize: '0.8rem' }}>
                <MenuItem value="contains" sx={{ fontSize: '0.8rem' }}>contains</MenuItem>
                <MenuItem value="equals" sx={{ fontSize: '0.8rem' }}>equals</MenuItem>
                <MenuItem value="startsWith" sx={{ fontSize: '0.8rem' }}>starts with</MenuItem>
                <MenuItem value="matches" sx={{ fontSize: '0.8rem' }}>matches (regex)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Expected URL / Value</Typography>
            <TextField size="small" fullWidth value={step.value || step.selector || ''} variant="outlined"
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
          </Box>
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'assert_element':
      return (
        <>
          <SelectorField value={step.selector} />
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'script':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Script Body</Typography>
            <TextField size="small" fullWidth multiline rows={4} value={step.value || ''} variant="outlined"
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
          </Box>
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'select':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Name</Typography>
            <TextField size="small" fullWidth value={step.name} variant="outlined" sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
          </Box>
          <SelectorField value={step.selector} />
          <ValueFieldWithCred value={step.value} label="Value" />
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    default:
      return (
        <>
          {step.selector && <SelectorField value={step.selector} />}
          {step.value && <ValueFieldWithCred value={step.value} />}
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
  }
};

const ExtensionModeMockup = () => {
  const [rightTab, setRightTab] = useState(0);
  const [expandedStep, setExpandedStep] = useState(null);
  const [dividerPos, setDividerPos] = useState(45);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(true); // Show the bar in mockup
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    const handleMouseMove = (moveEvent) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newPos = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      if (newPos >= 25 && newPos <= 75) {
        setDividerPos(newPos);
      }
    };
    const handleMouseUp = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  return (
    <Box ref={containerRef} sx={{ display: 'flex', height: 'calc(100vh - 180px)', gap: 0, userSelect: draggingRef.current ? 'none' : 'auto' }}>
      {/* LEFT PANEL - Extension info */}
      <Paper sx={{ width: `${dividerPos}%`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }} elevation={2}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
          <ExtensionIcon sx={{ color: '#1976d2', fontSize: 20, mr: 1 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>Extension Mode</Typography>
          <Chip label="Content Filter Policy Update" size="small" color="primary" variant="outlined" />
        </Box>

        {/* Extension explanation */}
        <Box sx={{
          flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          p: 4, textAlign: 'center',
        }}>
          {/* Browser illustration */}
          <Box sx={{
            width: '100%', maxWidth: 420, border: '2px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', mb: 4,
          }}>
            {/* Browser chrome */}
            <Box sx={{ bgcolor: '#f5f5f5', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ccc' }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ccc' }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ccc' }} />
              </Box>
              <Box sx={{ flexGrow: 1, bgcolor: '#fff', borderRadius: 1, px: 1.5, py: 0.3, fontSize: '0.7rem', color: '#999', border: '1px solid #e0e0e0' }}>
                https://admin.securly.com/content-filter
              </Box>
              <ExtensionIcon sx={{ fontSize: 16, color: '#1976d2' }} />
            </Box>
            {/* Page content with sidebar */}
            <Box sx={{ display: 'flex', height: 200 }}>
              {/* Main page area */}
              <Box sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ height: 12, width: '60%', bgcolor: '#e0e0e0', borderRadius: 1 }} />
                <Box sx={{ height: 8, width: '80%', bgcolor: '#eeeeee', borderRadius: 1 }} />
                <Box sx={{ height: 8, width: '70%', bgcolor: '#eeeeee', borderRadius: 1 }} />
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Box sx={{ height: 24, width: 60, bgcolor: '#e3f2fd', borderRadius: 1 }} />
                  <Box sx={{ height: 24, width: 60, bgcolor: '#f5f5f5', borderRadius: 1 }} />
                  <Box sx={{ height: 24, width: 60, bgcolor: '#f5f5f5', borderRadius: 1 }} />
                </Box>
                <Box sx={{ mt: 1, height: 50, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }} />
                <Box sx={{ height: 50, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }} />
              </Box>
              {/* Extension sidebar */}
              <Box sx={{ width: 120, bgcolor: '#1a237e', p: 1, display: 'flex', flexDirection: 'column', gap: 0.5, borderLeft: '2px solid #3949ab' }}>
                <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.6rem', mb: 0.5 }}>AUTOMATRIX</Typography>
                <Box sx={{ height: 6, width: '80%', bgcolor: '#3949ab', borderRadius: 0.5 }} />
                <Box sx={{ height: 6, width: '60%', bgcolor: '#3949ab', borderRadius: 0.5 }} />
                <Box sx={{ mt: 0.5 }}>
                  {[1,2,3,4].map(i => (
                    <Box key={i} sx={{ height: 10, bgcolor: i <= 2 ? '#4caf50' : i === 3 ? '#ffc107' : '#555', borderRadius: 0.5, mb: 0.3, opacity: 0.7 }} />
                  ))}
                </Box>
                <Box sx={{ mt: 'auto', height: 14, bgcolor: '#4caf50', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.5rem' }}>RUN</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Typography variant="h6" fontWeight={700} gutterBottom color="text.primary">
            Extension Sidebar is the Editor
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 380, mb: 3 }}>
            In Extension Mode, the Chrome Extension sidebar controls script execution directly
            in the user's browser tab. This web dashboard provides management, monitoring, and configuration.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, width: 180, textAlign: 'center' }}>
              <LaptopIcon sx={{ fontSize: 32, color: '#1976d2', mb: 1 }} />
              <Typography variant="subtitle2" fontWeight={700}>Extension Sidebar</Typography>
              <Typography variant="caption" color="text.secondary">
                Runs steps in real browser, interacts with actual DOM
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, width: 180, textAlign: 'center' }}>
              <DesktopIcon sx={{ fontSize: 32, color: '#7b1fa2', mb: 1 }} />
              <Typography variant="subtitle2" fontWeight={700}>This Dashboard</Typography>
              <Typography variant="caption" color="text.secondary">
                Edit steps, view history, configure settings, monitor runs
              </Typography>
            </Paper>
          </Box>

          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            sx={{ mt: 3 }}
          >
            Open Extension Sidebar
          </Button>
        </Box>

        {/* Bottom status bar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', px: 2, py: 1,
          borderTop: '1px solid #e0e0e0', bgcolor: '#fff8e1',
        }}>
          <RunningIcon sx={{ fontSize: 16, color: '#ff9800', mr: 1 }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: '#e65100' }}>
            Step 5/10: Select target policy
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Chip label="Extension Connected" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.7rem', height: 22 }} />
        </Box>
      </Paper>

      {/* DIVIDER */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          width: 8, cursor: 'col-resize', display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: draggingRef.current ? '#1976d2' : '#e0e0e0',
          '&:hover': { bgcolor: '#bbdefb' },
          transition: 'background-color 0.15s',
          flexShrink: 0,
        }}
      >
        <DragIcon sx={{ fontSize: 16, color: '#757575', transform: 'rotate(90deg)' }} />
      </Box>

      {/* RIGHT PANEL - Editor */}
      <Paper sx={{ width: `${100 - dividerPos}%`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }} elevation={2}>
        <Tabs value={rightTab} onChange={(_, v) => setRightTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: '1px solid #e0e0e0', minHeight: 42, '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontSize: '0.85rem' } }}
        >
          <Tab label="Steps" />
          <Tab label="Variables" />
          <Tab label="Settings" />
          <Tab label="History" />
          <Tab label="Output" />
        </Tabs>

        <Box sx={{ flexGrow: 1, overflow: 'auto', pb: rightTab === 0 && hasUnsavedChanges ? '64px' : 0 }}>
          {/* STEPS TAB */}
          <TabPanel value={rightTab} index={0}>
            {/* Transport bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
              <Tooltip title="Run All"><IconButton size="small" sx={{ bgcolor: '#4caf50', color: '#fff', '&:hover': { bgcolor: '#388e3c' } }}><PlayIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Step Forward"><IconButton size="small" sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' } }}><StepIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Pause"><IconButton size="small"><PauseIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Stop"><IconButton size="small"><StopIcon fontSize="small" /></IconButton></Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Tooltip title="Record Steps"><IconButton size="small" sx={{ color: '#f44336' }}><RecordIcon fontSize="small" /></IconButton></Tooltip>
              <Box sx={{ flexGrow: 1 }} />
              <LinearProgress variant="determinate" value={4/10*100} sx={{ width: 80, mr: 1 }} />
              <Typography variant="caption" color="text.secondary">4/10</Typography>
            </Box>

            {/* Credential dropdown */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Credential</InputLabel>
                <Select value="cred1" label="Credential">
                  <MenuItem value="cred1">Securly Admin - admin @ fivekeys</MenuItem>
                  <MenuItem value="cred2">Securly Admin - admin @ riverside</MenuItem>
                  <MenuItem value="cred3">Securly Admin - admin @ sandi</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Step list */}
            <Box sx={{ px: 1, py: 1 }}>
              {FILTER_STEPS.map((step, idx) => {
                const sc = STATUS_CONFIGS[step.status];
                const tc = TYPE_CHIP_COLORS[step.type] || TYPE_CHIP_COLORS.click;
                const isExp = expandedStep === step.num;
                return (
                  <Box key={step.num}>
                    <Box
                      onClick={() => setExpandedStep(isExp ? null : step.num)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                        borderRadius: 1, cursor: 'pointer', mb: 0.5,
                        bgcolor: sc.bg,
                        border: sc.border || '1px solid transparent',
                        '&:hover': { bgcolor: step.status === 'pending' ? '#f5f5f5' : sc.bg },
                      }}
                    >
                      {/* Drag handle */}
                      <Box sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: '#9e9e9e', '&:hover': { color: '#616161' }, mr: -0.5 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DragIcon sx={{ fontSize: 18 }} />
                      </Box>
                      {sc.icon}
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#757575', minWidth: 20 }}>
                        {step.num}
                      </Typography>
                      <Chip
                        label={step.type}
                        size="small"
                        sx={{ bgcolor: tc.bg, color: tc.color, fontWeight: 600, fontSize: '0.65rem', height: 20, textTransform: 'capitalize' }}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: step.status === 'running' ? 700 : 400 }}>
                        {step.name}
                      </Typography>
                      {isExp ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </Box>
                    {isExp && (
                      <Box sx={{ ml: 5, mb: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        {/* Step type chooser */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Step Type</Typography>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <Select value={step.type} variant="outlined" sx={{ fontSize: '0.85rem' }}>
                              {STEP_TYPES.map(t => (
                                <MenuItem key={t.value} value={t.value} sx={{ fontSize: '0.85rem' }}>{t.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Type-specific fields */}
                        <StepTypeFields step={step} steps={FILTER_STEPS} />

                        {/* Action buttons: Up, Down, Run, Delete */}
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Tooltip title="Move step up">
                            <span>
                              <IconButton size="small" disabled={idx === 0}>
                                <UpIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move step down">
                            <span>
                              <IconButton size="small" disabled={idx >= FILTER_STEPS.length - 1}>
                                <DownIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Run this step only">
                            <IconButton size="small" sx={{ color: '#4caf50' }}>
                              <PlayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Box sx={{ flexGrow: 1 }} />
                          <Tooltip title="Delete step">
                            <IconButton size="small" sx={{ color: '#f44336' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}

              <Button startIcon={<AddIcon />} size="small" sx={{ mt: 1, ml: 1 }} variant="outlined">
                Add Step
              </Button>
            </Box>
          </TabPanel>

          {/* VARIABLES TAB */}
          <TabPanel value={rightTab} index={1}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Script Variables</Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>Fill all variables before running</Alert>

              {/* Existing variables as editable rows */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                  <TextField label="Variable name" size="small" value="policy_name" sx={{ flex: 1 }} />
                  <TextField label="Value" size="small" placeholder="e.g. Default Student Policy" sx={{ flex: 1.5 }} />
                  <Tooltip title="Remove variable">
                    <IconButton size="small" sx={{ mt: 0.5, color: '#f44336' }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                  <TextField label="Variable name" size="small" value="allowed_domains" sx={{ flex: 1 }} />
                  <TextField label="Value" size="small" placeholder="e.g. khanacademy.org, readtheory.org" multiline rows={2} sx={{ flex: 1.5 }} />
                  <Tooltip title="Remove variable">
                    <IconButton size="small" sx={{ mt: 0.5, color: '#f44336' }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Button startIcon={<AddIcon />} size="small" variant="outlined" sx={{ mb: 2 }}>
                Add Variable
              </Button>

              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Variables are injected into step values using {'{{variable_name}}'} syntax.
                Credential fields (username, password) are auto-injected from the selected credential.
              </Typography>
            </Box>
          </TabPanel>

          {/* SETTINGS TAB */}
          <TabPanel value={rightTab} index={2}>
            <Box sx={{ p: 2 }}>
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>Execution Mode</FormLabel>
                <RadioGroup row value="frontend">
                  <FormControlLabel value="frontend" control={<Radio size="small" />} label="Frontend" />
                  <FormControlLabel value="backend" control={<Radio size="small" />} label="Backend" />
                  <FormControlLabel value="both" control={<Radio size="small" />} label="Both" />
                </RadioGroup>
              </FormControl>

              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Script Type</InputLabel>
                <Select value="content_interaction" label="Script Type">
                  <MenuItem value="login">Login</MenuItem>
                  <MenuItem value="progress">Progress Collection</MenuItem>
                  <MenuItem value="content_interaction">Content Interaction</MenuItem>
                  <MenuItem value="roster">Roster</MenuItem>
                  <MenuItem value="de-roster">De-roster</MenuItem>
                </Select>
              </FormControl>

              <TextField label="App Key" size="small" fullWidth value="securly" sx={{ mb: 3 }} InputProps={{ readOnly: true }} />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Chaining</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Prerequisite scripts that must run first:</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <Chip label="Securly Admin Login" onDelete={() => {}} size="small" color="primary" variant="outlined" />
                <Button size="small" startIcon={<AddIcon />}>Add</Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <FormControlLabel control={<Switch size="small" checked />} label="Teacher Mode" sx={{ mb: 2, display: 'block' }} />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Schedule</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">Not scheduled</Typography>
                <Button size="small" variant="outlined">Enable Schedule</Button>
              </Box>
            </Box>
          </TabPanel>

          {/* HISTORY TAB */}
          <TabPanel value={rightTab} index={3}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Execution History</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Credential</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Steps</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {MOCK_EXECUTIONS.map(exec => (
                    <TableRow key={exec.id}>
                      <TableCell><Typography variant="caption">{exec.date}</Typography></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {exec.status === 'passed' ? <PassIcon sx={{ color: '#4caf50', fontSize: 16 }} /> : <FailIcon sx={{ color: '#f44336', fontSize: 16 }} />}
                          <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{exec.status}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="caption">{exec.duration}</Typography></TableCell>
                      <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{exec.credential}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{exec.steps}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </TabPanel>

          {/* OUTPUT TAB */}
          <TabPanel value={rightTab} index={4}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Execution Output</Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#1a1a2e', borderRadius: 1, overflow: 'auto', maxHeight: 500 }}>
                <pre style={{ color: '#a5d6a7', fontFamily: 'monospace', fontSize: '0.8rem', margin: 0, whiteSpace: 'pre-wrap' }}>
{JSON.stringify(MOCK_OUTPUT, null, 2)}
                </pre>
              </Paper>
            </Box>
          </TabPanel>
        </Box>

        {/* Save/Cancel/Undo sticky bar - only on Steps tab with unsaved changes */}
        {rightTab === 0 && hasUnsavedChanges && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
            borderTop: '2px solid #ffc107', bgcolor: '#fffde7',
            position: 'sticky', bottom: 0, zIndex: 10,
          }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ffc107', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: '#f57f17', fontWeight: 700, mr: 1 }}>Unsaved changes</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Undo last change">
              <IconButton size="small"><UndoIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Button size="small" variant="outlined" color="inherit" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
              Discard Changes
            </Button>
            <Button size="small" variant="contained" startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontSize: '0.8rem', bgcolor: '#1976d2' }}
            >
              Save Script
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ExtensionModeMockup;
